import { useCallback, useRef, useState } from 'react';

function getAuthToken() {
  return localStorage.getItem('_at') || localStorage.getItem('token');
}

/**
 * WebSocket-only real-time connection hook.
 * No polling fallbacks; reconnects with bounded exponential backoff.
 */
export function useRealtimeConnection({
  endpoint,
  onMessage,
  onConnect,
  onDisconnect,
  onAuthenticated,
  maxReconnectAttempts = 3,
  reconnectDelay = 2000,
  enableWebSocket = true,
}) {
  const [connected, setConnected] = useState(false);
  const [authenticated, setAuthenticated] = useState(false);
  const [error, setError] = useState(null);
  const [connectionType, setConnectionType] = useState('none');

  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const authTimeoutRef = useRef(null);
  const shouldReconnectRef = useRef(false);
  const messageQueueRef = useRef([]);

  const clearTimers = () => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (authTimeoutRef.current) {
      clearTimeout(authTimeoutRef.current);
      authTimeoutRef.current = null;
    }
  };

  const cleanupSocket = () => {
    if (wsRef.current) {
      try {
        wsRef.current.close();
      } catch {
        // ignore
      }
      wsRef.current = null;
    }
  };

  const send = useCallback(
    data => {
      if (
        connectionType === 'websocket' &&
        wsRef.current?.readyState === WebSocket.OPEN &&
        authenticated
      ) {
        wsRef.current.send(JSON.stringify(data));
        return true;
      }

      messageQueueRef.current.push(data);
      return false;
    },
    [authenticated, connectionType]
  );

  const authenticateWebSocket = useCallback(() => {
    const token = getAuthToken();
    if (!token) {
      setError('No authentication token found');
      return false;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(
        JSON.stringify({
          type: 'auth',
          token,
        })
      );

      authTimeoutRef.current = setTimeout(() => {
        if (!authenticated && shouldReconnectRef.current) {
          setError('WebSocket authentication timed out');
          cleanupSocket();
          setConnected(false);
          setAuthenticated(false);
          setConnectionType('none');
          onDisconnect?.();
        }
      }, 5000);

      return true;
    }

    return false;
  }, [authenticated, onDisconnect]);

  const connectWebSocket = useCallback(() => {
    if (!enableWebSocket) {
      setError('WebSocket disabled');
      return;
    }

    const token = getAuthToken();
    if (!token) {
      setError('No authentication token found');
      return;
    }

    if (wsRef.current?.readyState === WebSocket.OPEN) return;

    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const { host } = window.location;
    const wsUrl = `${protocol}//${host}${endpoint}`;

    try {
      const ws = new WebSocket(wsUrl);
      wsRef.current = ws;

      ws.onopen = () => {
        setConnectionType('websocket');
        setConnected(true);
        setError(null);
        reconnectAttemptsRef.current = 0;
        authenticateWebSocket();
        onConnect?.();
      };

      ws.onmessage = event => {
        try {
          const data = JSON.parse(event.data);

          if (data.type === 'authenticated') {
            clearTimers();
            setAuthenticated(true);
            onAuthenticated?.(data);

            while (messageQueueRef.current.length > 0) {
              const queuedMessage = messageQueueRef.current.shift();
              ws.send(JSON.stringify(queuedMessage));
            }
            return;
          }

          if (data.type === 'error' && String(data.message || '').toLowerCase().includes('auth')) {
            setError(data.message || 'WebSocket authentication error');
            setAuthenticated(false);
            ws.close();
            return;
          }

          if (authenticated) {
            onMessage?.(data);
          }
        } catch {
          // ignore malformed messages
        }
      };

      ws.onclose = event => {
        clearTimers();
        setConnected(false);
        setAuthenticated(false);
        setConnectionType('none');
        wsRef.current = null;
        onDisconnect?.();

        if (!shouldReconnectRef.current) return;
        if (event.wasClean) return;

        if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
          setError('WebSocket disconnected');
          return;
        }

        const delay = Math.min(reconnectDelay * Math.pow(2, reconnectAttemptsRef.current), 30000);
        reconnectAttemptsRef.current += 1;
        reconnectTimeoutRef.current = setTimeout(connectWebSocket, delay);
      };

      ws.onerror = () => {
        setError('WebSocket connection error');
      };
    } catch (socketError) {
      setError(socketError?.message || 'WebSocket error');
    }
  }, [
    authenticateWebSocket,
    authenticated,
    enableWebSocket,
    endpoint,
    maxReconnectAttempts,
    onConnect,
    onDisconnect,
    onAuthenticated,
    onMessage,
    reconnectDelay,
  ]);

  const connect = useCallback(() => {
    shouldReconnectRef.current = true;
    reconnectAttemptsRef.current = 0;
    connectWebSocket();
  }, [connectWebSocket]);

  const disconnect = useCallback(() => {
    shouldReconnectRef.current = false;
    clearTimers();
    messageQueueRef.current = [];
    cleanupSocket();

    setConnected(false);
    setAuthenticated(false);
    setConnectionType('none');
    setError(null);
  }, []);

  return {
    connected,
    authenticated,
    error,
    connectionType,
    connect,
    disconnect,
    send,
  };
}

export default useRealtimeConnection;
